import produce from 'immer';
import React, {useCallback, useMemo} from 'react';

import {assert} from 'Asserts';
import {MethodUpdater as LegacyMethodUpdater} from 'procedures/design/LegacyProcedureEditor';
import MethodEditor from 'procedures/design/method-config/MethodEditor';
import {
  ProcedureValidationContext,
  ProcedureValidationContextValue,
} from 'procedures/design/validation/ProcedureValidationContext';
import {
  ProcedureValidationErrorType,
  ProcedureValidationObjectType,
} from 'procedures/design/validation/ProcedureValidationTypes';
import {
  filterProcedureValidationErrors,
  findRelevantProcedureValidationErrors,
} from 'procedures/design/validation/procedureValidationUtils';
import {Method} from 'procedures/ProcedureDomainTypes';
import ProcedureFlowchartValidator from 'procedures/procedures-platform/ProcedureFlowchartValidator';
import {isMethodNode, isNodeValidInProcedureFlowchart} from 'procedures/processes/MethodNodeUtils';
import AutosaveManager from 'procedures/redux/autosaveManager';
import {
  TaskDefinitionVersionSpecWithTaskSchemaFlowchart,
  TaskNodeDetailsSidepanelContentProps,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';
import {getApplicationDataSafely} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorNodeTypes';
import {ProcedurePauseCondition} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/redux/reduceFlowchartConfig';
import useOnMount from 'ui-library/hooks/useOnMount';
import useStateIfMounted from 'ui-library/hooks/useStateIfMounted';

import {getMaterialFlowsFromProcessFlowchart} from './DataFlowEdgeUtils';
import MethodSidepanelContext from './MethodSidepanelContext';
import useValidateBeforeSave from './useValidateBeforeSave';

export default function MethodSidepanelEditor(
  props: TaskNodeDetailsSidepanelContentProps<Method>
): JSX.Element {
  const {selectedNodeTaskDefinitionVersionSpec, setIsAutosaverPaused} = props;
  const taskDefinitionVersion = selectedNodeTaskDefinitionVersionSpec.newProcessTaskDefinitionVersion;

  useOnMount(() => {
    assert(
      Object.values(props.flowchart.nodes).every((node) => isNodeValidInProcedureFlowchart(node)),
      'MethodSidepanelEditor requires a flowchart of nodes that can be used in Procedures'
    );
  });

  const onUpdateTaskDefinitionVersion = props.onUpdateTaskDefinitionVersion;
  const {applicationData: method} = taskDefinitionVersion;
  assert(method != null, 'MethodSidepanelEditor requires a Method task definition version');

  const onChangeMethod = useCallback(
    (_cid: string, methodUpdater: LegacyMethodUpdater): void => {
      onUpdateTaskDefinitionVersion(
        (currentFlowchart: TaskDefinitionVersionSpecWithTaskSchemaFlowchart<Method>) => {
          const currentNodeSpec = currentFlowchart.nodes[props.selectedNodeId];

          assert(
            currentNodeSpec != null,
            `Node ${selectedNodeTaskDefinitionVersionSpec.clientId} not found in current flowchart`
          );

          const currentMethodData = getApplicationDataSafely<Method>(currentNodeSpec);
          const newMethodData = methodUpdater(currentMethodData);

          return produce(currentNodeSpec, (draftVersion) => {
            draftVersion.newProcessTaskDefinitionVersion.applicationData = newMethodData;
          });
        },
        false
      );
    },
    [selectedNodeTaskDefinitionVersionSpec, onUpdateTaskDefinitionVersion, props.selectedNodeId]
  );

  const saveImmediately = useCallback(
    async (methodData: Method): Promise<void> => {
      await onUpdateTaskDefinitionVersion(
        (currentFlowchart: TaskDefinitionVersionSpecWithTaskSchemaFlowchart<Method>) => {
          const currentNodeSpec = currentFlowchart.nodes[props.selectedNodeId];

          assert(currentNodeSpec != null, `Node ${props.selectedNodeId} not found in current flowchart`);

          return produce(currentNodeSpec, (draftVersion) => {
            draftVersion.newProcessTaskDefinitionVersion.applicationData = methodData;
          });
        },
        true
      );
    },
    [onUpdateTaskDefinitionVersion, props.selectedNodeId]
  );

  const pauseAutosaver = useCallback(
    (condition: ProcedurePauseCondition, value: boolean) => {
      setIsAutosaverPaused(condition, value);
    },
    [setIsAutosaverPaused]
  );

  const flushUnsavedChanges = useCallback(async () => {
    await AutosaveManager.flushUnsavedChanges(props.flowchartConfigId);
  }, [props.flowchartConfigId]);

  const validateBeforeSave = useValidateBeforeSave({
    flowchartConfigId: props.flowchartConfigId,
    selectedNodeId: props.selectedNodeId,
  });

  const allMethodNames = Object.values(props.flowchart.nodes)
    .filter((node) => isMethodNode(node))
    .map((node) => {
      const applicationData = getApplicationDataSafely<Method>(node);
      return {cid: applicationData.cid, name: applicationData.name};
    });
  const materialFlows = useMemo(
    () => getMaterialFlowsFromProcessFlowchart(props.flowchart),
    [props.flowchart]
  );

  // Convert ProcessFlowchartValidationErrors to ProcedureValidationErrors for MethodEditor.
  // MethodEditor currently only understands ProcedureValidationErrors, so we need to convert
  // the processes platform errors back to the procedure format.
  // TODO(BNCH-141566): This conversion can be removed once MethodEditor is migrated to handle
  // ProcessFlowchartValidationErrors directly.
  const allProcedureValidationErrors = useMemo(
    () =>
      ProcedureFlowchartValidator.convertProcessValidationErrorsToProcedureErrors(
        props.validationErrors,
        props.flowchart
      ),
    [props.validationErrors, props.flowchart]
  );
  const selectedMethodValidationErrors = useMemo(
    () => [
      ...findRelevantProcedureValidationErrors(allProcedureValidationErrors, {
        id: method.cid,
        objectType: ProcedureValidationObjectType.METHOD,
      }),
      // Procedure-wide product limit errors (e.g. total parameters across all methods) have no
      // method association, so findRelevantProcedureValidationErrors won't match them. Include
      // them separately. Per-method product limit errors are already included above via parentObjects.
      ...filterProcedureValidationErrors(allProcedureValidationErrors, {
        errorTypes: [ProcedureValidationErrorType.PROCEDURE_PRODUCT_LIMITS],
      }).filter((e) => e.parentObjects.length === 0),
    ],
    [allProcedureValidationErrors, method.cid]
  );

  const [anyTabValidationErrors, setAnyTabValidationErrors] = useStateIfMounted(false);
  const validationContextValue = useMemo<ProcedureValidationContextValue>(
    () => ({
      procedureValidationErrors: selectedMethodValidationErrors,
      anyTabValidationErrors,
      setAnyTabValidationErrors,
    }),
    [selectedMethodValidationErrors, anyTabValidationErrors, setAnyTabValidationErrors]
  );

  const methodSidepanelContextValue = useMemo(
    () => ({
      saveImmediately,
      pauseAutosaver,
      flushUnsavedChanges,
      materialFlows,
      validateBeforeSave,
    }),
    [saveImmediately, pauseAutosaver, flushUnsavedChanges, materialFlows, validateBeforeSave]
  );

  return (
    <MethodSidepanelContext.Provider value={methodSidepanelContextValue}>
      {/* TODO(BNCH-141566): Remove this once MethodEditor is migrated to handle ProcessFlowchartValidationErrors directly. */}
      <ProcedureValidationContext.Provider value={validationContextValue}>
        <MethodEditor
          // Rebuild the method editor if the selected node changes
          key={props.selectedNodeTaskDefinitionVersionSpec.clientId}
          selectedMethod={method}
          onChangeMethod={onChangeMethod}
          // TODO: these 3 props need proper hydration via the workflows platform
          isProcedureReadOnly={Boolean(props.locked)}
          isProcedureArchived={false}
          allMethodNames={allMethodNames}
        />
      </ProcedureValidationContext.Provider>
    </MethodSidepanelContext.Provider>
  );
}
